import { BufferGeometry } from '../containers/BufferGeometry.js';
import { BufferAttribute } from '../../attributes/BufferAttribute.js';
import { Vector3 } from '../../../math/Vector3.js';
import { Vector2 } from '../../../math/Vector2.js';
/**
 * @param { number } radiusTop Radius of the cylinder at the top. Default is 1.
 * @param { number } radiusBottom Radius of the cylinder at the bottom. Default is 1.
 * @param { number } height Height of the cylinder. Default is 1.
 * @param { number } radialSegments Number of segmented faces around the circumference of the cylinder. Default is 8
 * @param { number } heightSegments Number of rows of faces along the height of the cylinder. Default is 1.
 * @param { number } openEnded A Boolean indicating whether the ends of the cylinder are open or capped. Default is false, meaning capped.
 * @param { number } thetaStart Start angle for first segment, default = 0 (three o'clock position).
 * @param { number } thetaLength The central angle, often called theta, of the circular sector. The default is 2*Pi, which makes for a complete cylinder.
 */
export interface CylinderShapeParameter {
    radiusTop: number;
    radiusBottom: number;
    height: number;
    radialSegments: number;
    heightSegments: number;
    openEnded: boolean;
    thetaStart: number;
    thetaLength: number;
}

export function cylinder(param: Partial<CylinderShapeParameter>): BufferGeometry {
    return new CylinderBufferGeometry(...(Object.values(param) as any));
}

export function cone(params: Omit<Partial<CylinderShapeParameter>, 'radiusTop'>) {
    return cylinder({ radiusTop: 0, ...params });
}

class CylinderBufferGeometry extends BufferGeometry {
    parameters: {
        radiusTop: number;
        radiusBottom: number;
        height: number;
        radialSegments: number;
        heightSegments: number;
        openEnded: boolean;
        thetaStart: number;
        thetaLength: number;
    };

    constructor(
        radiusTop: number = 1,
        radiusBottom: number = 1,
        height: number = 1,
        radialSegments: number = 8,
        heightSegments: number = 1,
        openEnded: boolean = false,
        thetaStart: number = 0,
        thetaLength: number = Math.PI * 2,
    ) {
        super();
        this.type = 'CylinderBufferGeometry';

        this.parameters = {
            radiusTop,
            radiusBottom,
            height,
            radialSegments,
            heightSegments,
            openEnded,
            thetaStart,
            thetaLength,
        };

        const scope = this;

        radialSegments = Math.floor(radialSegments);
        heightSegments = Math.floor(heightSegments);

        // buffers
        const indices: number[] = [];
        const vertices: number[] = [];
        const normals: number[] = [];
        const uvs: number[] = [];

        // helper variables
        let index = 0;
        let groupStart = 0;
        const indexArray: number[][] = [];
        const halfHeight = height / 2;

        // generate geometry
        generateTorso();

        if (openEnded === false) {
            if (radiusTop > 0) {
                generateCap(true);
            }
            if (radiusBottom > 0) {
                generateCap(false);
            }
        }

        // build geometry
        this.setIndex(indices);
        this.addAttribute('position', new BufferAttribute(new Float32Array(vertices), 3));
        this.addAttribute('normal', new BufferAttribute(new Float32Array(normals), 3));
        this.addAttribute('uv', new BufferAttribute(new Float32Array(uvs), 2));

        function generateTorso() {
            let x, y;
            const normal = new Vector3();
            const vertex = new Vector3();
            let groupCount = 0;

            // this will be used to calculate the normal
            const slope = (radiusBottom - radiusTop) / height;

            // generate vertices, normals and uvs
            for (y = 0; y <= heightSegments; y++) {
                const indexRow = [];
                const v = y / heightSegments;

                // calculate the radius of the current row
                const radius = v * (radiusBottom - radiusTop) + radiusTop;
                for (x = 0; x <= radialSegments; x++) {
                    const u = x / radialSegments;
                    const theta = u * thetaLength + thetaStart;
                    const sinTheta = Math.sin(theta);
                    const cosTheta = Math.cos(theta);

                    // vertex
                    vertex.x = radius * sinTheta;
                    vertex.y = -v * height + halfHeight;
                    vertex.z = radius * cosTheta;
                    vertices.push(vertex.x, vertex.y, vertex.z);

                    // normal
                    normal.set(sinTheta, slope, cosTheta).normalize();
                    normals.push(normal.x, normal.y, normal.z);

                    // uv
                    uvs.push(u, 1 - v);

                    // save index of vertex in respective row
                    indexRow.push(index++);
                }

                // now save vertices of the row in our index array
                indexArray.push(indexRow);
            }

            // generate indices
            for (x = 0; x < radialSegments; x++) {
                for (y = 0; y < heightSegments; y++) {
                    // we use the index array to access the correct indices
                    const a = indexArray[y][x];
                    const b = indexArray[y + 1][x];
                    const c = indexArray[y + 1][x + 1];
                    const d = indexArray[y][x + 1];

                    // faces
                    indices.push(a, b, d);
                    indices.push(b, c, d);

                    // update group counter
                    groupCount += 6;
                }
            }
            // add a group to the geometry. this will ensure multi material support
            scope.addGroup(groupStart, groupCount, 0);

            // calculate new start value for groups
            groupStart += groupCount;
        }

        function generateCap(top: boolean) {
            let x;
            const uv = new Vector2();
            const vertex = new Vector3();
            let groupCount = 0;
            const radius = top === true ? radiusTop : radiusBottom;
            const sign = top === true ? 1 : -1;

            // save the index of the first center vertex
            const centerIndexStart = index;
            // first we generate the center vertex data of the cap.
            // because the geometry needs one set of uvs per face,
            // we must generate a center vertex per face/segment
            for (x = 1; x <= radialSegments; x++) {
                // vertex
                vertices.push(0, halfHeight * sign, 0);
                // normal
                normals.push(0, sign, 0);
                // uv
                uvs.push(0.5, 0.5);
                // increase index
                index++;
            }
            // save the index of the last center vertex
            const centerIndexEnd = index;
            // now we generate the surrounding vertices, normals and uvs
            for (x = 0; x <= radialSegments; x++) {
                const u = x / radialSegments;
                const theta = u * thetaLength + thetaStart;
                const cosTheta = Math.cos(theta);
                const sinTheta = Math.sin(theta);

                // vertex
                vertex.x = radius * sinTheta;
                vertex.y = halfHeight * sign;
                vertex.z = radius * cosTheta;
                vertices.push(vertex.x, vertex.y, vertex.z);

                // normal
                normals.push(0, sign, 0);

                // uv
                uv.x = cosTheta * 0.5 + 0.5;
                uv.y = sinTheta * 0.5 * sign + 0.5;
                uvs.push(uv.x, uv.y);

                // increase index
                index++;
            }

            // generate indices
            for (x = 0; x < radialSegments; x++) {
                const c = centerIndexStart + x;
                const i = centerIndexEnd + x;
                if (top === true) {
                    // face top
                    indices.push(i, i + 1, c);
                } else {
                    // face bottom
                    indices.push(i + 1, i, c);
                }
                groupCount += 3;
            }

            // add a group to the geometry. this will ensure multi material support
            scope.addGroup(groupStart, groupCount, top === true ? 1 : 2);

            // calculate new start value for groups
            groupStart += groupCount;
        }
    }
}
